/**
 * PassportDiagnosticCompetency Component
 * 
 * Displays Diagnostic Competency Score (DCS) and related metrics.
 *
 * WHO SEES THIS, AND WHY IT CHANGED
 *
 * This component used to render for managers alone — `if (!isManagerView)
 * return null` — with a footer stating "Technicians do not see numeric DCS
 * scores at this time".
 *
 * CORE-C's constitutional guarantee is that the subject can see what the
 * record says about them, and CORE-C-GAP-001 recorded this section as the
 * violation: information shown to others and not to the person is, in the
 * legacy law's own words, "a file". Product ruling 2026-09-07: the
 * manager-only suppression is removed for the SELF view, including the same
 * substantive competency facts a manager sees, without widening disclosure
 * to anybody else.
 *
 * So there are three viewers, not two:
 *
 *   the subject   sees their own competency in full
 *   a manager     unchanged
 *   anyone else   still sees nothing, not even a placeholder
 *
 * The database half landed first (20261114130000); before it, this
 * component would have rendered an empty card to the subject because the
 * row-level policy withheld their own rows from them.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Brain, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTechnicianDiagnosticStats } from '@/hooks/useTechnicianDiagnosticStats';
import { 
  getSampleStatusLabel, 
  getSampleStatusColor,
  getDCSQualityTier,
} from '@/utils/diagnosticScoringEngine';
import { cn } from '@/lib/utils';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';
import { PassportCompetencyAnalytics } from './PassportCompetencyAnalytics';
import { competencyAnalyticsFromRow } from '@/lib/passport/competencyAnalytics';

interface PassportDiagnosticCompetencyProps {
  technicianProfileId: string;
  /** A manager of a shop in this person’s employment history. */
  isManagerView: boolean;
  /**
   * The person this record is about, reading their own Passport.
   * CORE-C-GAP-001: the subject sees what the record says about them.
   */
  isSelfView?: boolean;
  /**
   * Oct 20 addendum P1/P2: a read-only in-scope viewer — a Service Advisor of the person's shop or an ORG_ADMIN of
   * its organisation. The server (can_view_technician_passport) decides; this only chooses presentation.
   */
  isInScopeViewer?: boolean;
}

export function PassportDiagnosticCompetency({
  technicianProfileId,
  isManagerView,
  isSelfView = false,
  isInScopeViewer = false,
}: PassportDiagnosticCompetencyProps) {
  const {
    data: stats,
    isLoading,
    isError,
  } = useTechnicianDiagnosticStats(technicianProfileId);

  // Everyone who is neither the subject nor a manager still sees nothing,
  // not even a placeholder. Removing the suppression for the subject must
  // not widen it to anybody else.
  if (!isManagerView && !isSelfView && !isInScopeViewer) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Diagnostic Competency
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Failure is not a fact.
  //
  // `useQuery` leaves `data` undefined when the fetch fails, so a read that
  // could not complete used to fall through to "No diagnostic data captured
  // yet" — telling a person their record is empty when the truth is that it
  // could not be read. That is the difference between an absence and an
  // unknown, and it matters most to the person the record is about.
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Diagnostic Competency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Diagnostic competency could not be loaded</p>
            <p className="text-xs mt-1">
              This is a problem reading the record, not a statement about it.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data yet
  if (!stats || stats.totalDiagnostics === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Diagnostic Competency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <Brain className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No diagnostic data captured yet</p>
            <p className="text-xs mt-1">
              Diagnostic competency scores will appear after diagnostic jobs are completed
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const dcs = stats.overallDCS ?? 50;
  const qualityTier = getDCSQualityTier(dcs);
  // Oct 20 S2 — CORE-I :124/:49: a record with no dedicated diagnostic evidence
  // (e.g. inspections only) is not presented as a competency score at all.
  const analytics = competencyAnalyticsFromRow(stats.overall);
  const notEstablished = analytics !== null && !analytics.establishedByDedicatedEvidence;
  const sampleStatusLabel = getSampleStatusLabel(stats.overallSampleStatus);
  const sampleStatusColor = getSampleStatusColor(stats.overallSampleStatus);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Diagnostic Competency
          </CardTitle>
          <Badge className={sampleStatusColor}>
            {analytics
              ? `${sampleStatusLabel} (${analytics.activity.dedicatedJobs} diagnostics · ${analytics.activity.inspectionJobs} inspections)`
              : `${sampleStatusLabel} (${stats.totalDiagnostics} diagnostics)`}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall DCS Score */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2">
              Diagnostic Competency Score (DCS)
              {/* CORE-C GAP-002. Beside the SCORE, not in the card header -
                  the header already carries the sample-status badge, and this
                  card also shows an average diagnostic time whose evidence
                  class CORE-I never declared. A card-level badge would say
                  OptiBay calculated those minutes, which it did not. */}
              <PassportProvenanceBadge factGroupId="diagnostic-competency-score" />
            </span>
            {notEstablished ? (
              <span className="text-sm font-semibold text-muted-foreground" data-testid="dcs-not-established">
                Not established
              </span>
            ) : (
              <span className={cn('text-2xl font-bold', qualityTier.color)}>
                {dcs.toFixed(0)}
              </span>
            )}
          </div>
          {notEstablished ? (
            <p className="text-xs text-muted-foreground">
              No dedicated diagnostic jobs with a recorded outcome yet. Inspection activity is shown below
              but does not by itself establish diagnostic competency.
            </p>
          ) : (
            <>
              <Progress value={dcs} className="h-3" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Needs Improvement</span>
                <span className={qualityTier.color}>{qualityTier.label}</span>
                <span>Exceptional</span>
              </div>
            </>
          )}
        </div>

        {/* Oct 20 S2-C — activity, uncapped and credited contributions, separately. */}
        <PassportCompetencyAnalytics overall={stats.overall} />

        {/* Score Breakdown (if overall stats available) */}
        {stats.overall && (
          <div className="grid grid-cols-2 gap-4">
            <ScoreCard
              label="Calibration"
              value={stats.overall.calibration_score}
              description="Confidence vs outcome alignment"
              provenanceFactGroupId="diagnostic-calibration"
            />
            <ScoreCard
              label="Outcome Success"
              value={stats.overall.outcome_success_rate}
              description="Correct diagnosis rate"
              suffix="%"
            />
            <ScoreCard
              label="Complaint Validation"
              value={stats.overall.complaint_validation_rate}
              description="Confirmed customer concerns"
              suffix="%"
            />
            <ScoreCard
              label="Avg Diagnostic Time"
              value={stats.overall.avg_diagnostic_minutes}
              description="Minutes per diagnostic"
              suffix=" min"
              isTime
            />
          </div>
        )}

        {/* Top Categories */}
        {stats.topCategories.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Strengths
            </h4>
            <div className="space-y-1">
              {stats.topCategories.map((cat) => (
                <div
                  key={cat.category}
                  className="flex items-center justify-between text-sm px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-900/20"
                >
                  <span className="capitalize">{cat.category.replace(/_/g, ' ')}</span>
                  <span className="font-medium text-emerald-700 dark:text-emerald-300">
                    {cat.dcs.toFixed(0)} DCS
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Improvement Areas */}
        {stats.improvementAreas.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Growth Areas
            </h4>
            <div className="space-y-2">
              {stats.improvementAreas.map((area) => (
                <div
                  key={area.category}
                  className="text-sm px-2 py-2 rounded bg-amber-50 dark:bg-amber-900/20"
                >
                  <div className="flex items-center justify-between">
                    <span className="capitalize font-medium">
                      {area.category.replace(/_/g, ' ')}
                    </span>
                    <span className="text-amber-700 dark:text-amber-300">
                      {area.dcs.toFixed(0)} DCS
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {area.recommendation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Who else can see this. The old note said "only visible to
            managers", which stopped being true when the subject gained
            sight of their own record. Saying it to the subject would have
            been telling them they cannot see the thing they are reading. */}
        <div className="text-xs text-muted-foreground border-t pt-3 mt-4">
          {isSelfView && !isManagerView ? (
            <>
              <strong>Who sees this:</strong> you; owners, managers and service
              advisors of the shops you currently work at; and your organization's
              administrators, read-only. It is not shown to fellow technicians or
              to shops you are not employed by.
            </>
          ) : (
            <>
              <strong>Who sees this:</strong> the person themselves; owners,
              managers and service advisors of the shops they currently work at;
              and their organization's administrators, read-only.
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Individual score card component
 */
function ScoreCard({
  label,
  value,
  description,
  suffix = '',
  isTime = false,
  provenanceFactGroupId,
}: {
  label: string;
  value: number | null;
  description: string;
  suffix?: string;
  isTime?: boolean;
  /**
   * Optional, and left unset on purpose for most cards in this grid. CORE-I
   * declares the calibration a Derivation; it declares nothing about the
   * outcome rates or the average diagnostic minutes, so those carry no badge
   * rather than inheriting their neighbour's.
   */
  provenanceFactGroupId?: string;
}) {
  const displayValue = value !== null ? value.toFixed(isTime ? 0 : 0) : '—';
  
  // Determine trend icon (placeholder - would need historical data)
  const TrendIcon = Minus;
  
  return (
    <div className="p-3 rounded-lg bg-muted/50">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <TrendIcon className="h-3 w-3 text-muted-foreground" />
      </div>
      {provenanceFactGroupId && (
        <div className="mb-1">
          <PassportProvenanceBadge factGroupId={provenanceFactGroupId} />
        </div>
      )}
      <div className="text-lg font-semibold">
        {displayValue}{value !== null ? suffix : ''}
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
